import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import constant from "config/constant";
import { wrapBaseApi } from "util/apiWrapper";
import ls from "util/secureStorage";

// console.log("AUTH HEADER", _headers);

// Define a service using a base URL and expected endpoints
export const p2pApi = createApi({
  reducerPath: "p2p",
  // refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "contractByTradeRef",
    "listing",
    "p2pListinUserProfile",
    "ownP2PProfile",
    "togglePresence",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_WALLET_API_ENDPOINT}`,
    prepareHeaders: (headers, _) => {
      const auth = ls.get(constant.AUTH_TOKEN_KEY);

      headers.set("Authorization", auth.token);
      headers.set("Content-Type", "application/json");
      headers.set("Cache-Control", "no-cache, no-store, must-revalidate");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getSavedBanks: builder.query({
      query: () => wrapBaseApi(`/p2p/banks`),
      transformResponse: (res) => res?.data,
      providesTags: ["savedP2PBanks"],
    }),
    getSellListingById: builder.query({
      query: (id) => wrapBaseApi(`/p2p/flexilisting/${id}/buy`),
      transformResponse: (res) => res?.data,
      // providesTags: ["BanksByCountry"],
    }),
    getCompletedTradeById: builder.query({
      query: (id) => wrapBaseApi(`/p2p/flexilisting/${id}/details`),
      transformResponse: (res) => res?.data,
      // providesTags: ["BanksByCountry"],
    }),
    getBuyTradeSession: builder.query({
      query: (id) => wrapBaseApi(`/p2p/flexilisting/contracts/${id}`),
      transformResponse: (res) => res?.data,
      providesTags: ["contractByTradeRef"],
    }),
    getListingById: builder.query({
      query: (id) => wrapBaseApi(`/p2p/flexiListing/${id}`),
      transformResponse: (res) => res?.data,
      // providesTags: ["contractByTradeRef"],
    }),
    getListing: builder.query({
      query: (path) => wrapBaseApi(`/p2p/flexiListing${path}`),
      transformResponse: (res) => res?.data,
      providesTags: ["listing"],
    }),
    getP2PProfile: builder.query({
      query: () => wrapBaseApi(`/p2p/profile`),
      transformResponse: (res) => res?.data,
      providesTags: ["ownP2PProfile"],
    }),
    getP2PListingUserProfile: builder.query({
      query: (username) => wrapBaseApi(`/p2p/profile/${username}`),
      transformResponse: (res) => res?.data,
      providesTags: ["p2pListinUserProfile"],
    }),
    updateTradeSession: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/p2p/flexiListing/flexiTrades/${input.id}/update`),
        method: "POST",
        body: {
          frozenRef: input.frozenRef,
          operation: input.operation,
          reasonForFlag: input.reason,
          paymentProof: input.paymentProof,
        },
      }),
      transformResponse: (res) => res.data,
      invalidatesTags: ["contractByTradeRef"],
    }),
    rateTrade: builder.mutation({
      query: (input) => {
        console.log("RATE TRADE INPUT", input);
        return {
          url: wrapBaseApi(`/p2p/flexiListing/${input.id}/rate`),
          method: "POST",
          body: {
            frozenRef: input.frozenRef,
            rating: input.rating,
          },
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["contractByTradeRef"],
    }),
    togglePresence: builder.mutation({
      query: (input) => {
        const action =
          String(input).trim().toLowerCase() === "online"
            ? "Online"
            : "Offline";
        return {
          url: wrapBaseApi(`/p2p/profiles/go${action}`),
          method: "POST",
          body: {},
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["togglePresence"],
    }),
    createBuyListing: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/p2p/flexilisting/${input.type ?? "buy"}`),
        method: "POST",
        body: input,
      }),
      transformResponse: (res) => res,
      invalidatesTags: ["listing"],
    }),
    updateListing: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/p2p/flexilisting/${input.id}/update`),
        method: "PUT",
        body: { ...input, id: null },
      }),
      transformResponse: (res) => res,
      invalidatesTags: ["listing"],
    }),
    buySellListing: builder.mutation({
      query: (input) => {
        console.log("INPUT", input);
        return {
          url: wrapBaseApi(`/p2p/flexilisting/${input.id}/buy`),
          method: "POST",
          body: input,
        };
      },
      transformResponse: (res) => res.data,
      // invalidatesTags: ["BanksDetails"],
    }),
    sellToBuyListing: builder.mutation({
      query: (input) => {
        console.log("INPUT", input);
        return {
          url: wrapBaseApi(`/p2p/flexilisting/${input.id}/sell`),
          method: "POST",
          body: {
            volume: input.volume,
            receiveIntoInternalWallet: input.receiveIntoInternalWallet,
            paymentOption: input.paymentOption,
          },
        };
      },
      transformResponse: (res) => res.data,
      // invalidatesTags: ["BanksDetails"],
    }),
    getTradeChats: builder.query({
      query: (id) => wrapBaseApi(`/p2p/chats/${id}`),
      transformResponse: (res) => res,
    }),
    getTradeChatsV2: builder.mutation({
      query: ({ channelName }) => ({
        url: wrapBaseApi(`/p2p/chats/${channelName}/get`),
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => (res?.error ? res : res?.data?.chats),
    }),
    cancelTrade: builder.mutation({
      query: (tradeId) => ({
        url: wrapBaseApi(`/p2p/flexiListing/${tradeId}/cancelListing`),
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => res.data,
      invalidatesTags: ["listing"],
    }),
    hideListing: builder.mutation({
      query: ({ tradeId, visibility = "hide" }) => ({
        url: wrapBaseApi(`/p2p/flexilisting/${tradeId}/${visibility}`),
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => res.data,
      invalidatesTags: ["listing"],
    }),
    initTradeChat: builder.mutation({
      query: ({ channelName }) => ({
        url: wrapBaseApi(`/p2p/chats/${channelName}/init`),
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => res,
    }),
    leaveTradeChat: builder.mutation({
      query: ({ channelName }) => ({
        url: wrapBaseApi(`/p2p/chats/${channelName}/leave`),
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => res,
    }),
    ringTrader: builder.mutation({
      query: ({ channelName, userIdToRing }) => ({
        url: wrapBaseApi(`/p2p/chats/${channelName}/ringUser`),
        method: "POST",
        body: { userIdToRing },
      }),
      transformResponse: (res) => res,
    }),
    sendTradeChat: builder.mutation({
      query: (input) => ({
        url: wrapBaseApi(`/p2p/chats/send`),
        method: "POST",
        body: { ...input },
      }),
      transformResponse: (res) => (res?.error ? res : res?.data?.message),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetListingQuery,
  useGetP2PProfileQuery,
  useGetListingByIdQuery,
  useGetP2PListingUserProfileQuery,
  useGetCompletedTradeByIdQuery,
  useGetBuyTradeSessionQuery,
  useGetSellListingByIdQuery,
  useGetSavedBanksQuery,
  useRateTradeMutation,
  useTogglePresenceMutation,
  useSellToBuyListingMutation,
  useBuySellListingMutation,
  useGetTradeChatsQuery,
  useCancelTradeMutation,
  useHideListingMutation,
  useSendTradeChatMutation,
  useInitTradeChatMutation,
  useGetTradeChatsV2Mutation,
  useLeaveTradeChatMutation,
  useRingTraderMutation,
  useUpdateListingMutation,
  useCreateBuyListingMutation,
  useUpdateTradeSessionMutation,
} = p2pApi;

export default p2pApi;
